import { useEffect } from "react";
import { motion } from "framer-motion";
import { X, Instagram, Mail, ExternalLink, ShoppingBag, Truck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { IG_DM_URL, VINTED_URL, imgSrc } from "@/lib/constants";
import { conditionStyle } from "./ProductCatalog";

export const ProductDetailModal = ({ product, onClose, onEmail }) => {
  const { t, lang } = useLang();

  useEffect(() => {
    if (product?.id) api.post("/track", { product_id: product.id, event: "view" }).catch(() => {});
  }, [product?.id]);

  if (!product) return null;
  const status = product.status || (product.is_available ? "available" : "sold");
  const sold = status === "sold";
  const reserved = status === "reserved";
  const description = (lang === "de" ? product.description_de : product.description_en) || product.description_de;

  const buyViaVinted = () => {
    api.post("/track", { product_id: product.id, event: "vinted_click" }).catch(() => {});
    window.open(VINTED_URL, "_blank", "noopener,noreferrer");
  };

  const askInstagram = () => {
    api.post("/track", { product_id: product.id, event: "ig_click" }).catch(() => {});
    const msg = t.dm(product);
    if (navigator.clipboard) navigator.clipboard.writeText(msg).catch(() => {});
    toast.success(t.product.copied);
    window.open(IG_DM_URL, "_blank", "noopener,noreferrer");
  };

  const emailInquiry = () => {
    api.post("/track", { product_id: product.id, event: "email_click" }).catch(() => {});
    onEmail(product);
  };

  return (
    <motion.div
      data-testid="product-modal-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-[#050A12]/75 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        data-testid="product-detail-modal"
        initial={{ opacity: 0, scale: 0.96, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 12 }}
        transition={{ type: "spring", stiffness: 260, damping: 26 }}
        onClick={(e) => e.stopPropagation()}
        className="relative grid max-h-[90vh] w-full max-w-4xl grid-cols-1 overflow-y-auto border border-edge-bright bg-surface shadow-[0_24px_64px_rgba(0,0,0,0.5)] md:grid-cols-2"
      >
        <button
          data-testid="modal-close-button"
          onClick={onClose}
          aria-label={t.product.close}
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center border border-edge bg-[#0B1526]/85 text-mutedink backdrop-blur-sm transition-colors hover:border-volt hover:text-volt"
        >
          <X size={16} />
        </button>
        <div className="relative min-h-[280px] overflow-hidden bg-[#101B30] p-3">
          <div className="relative h-full w-full overflow-hidden border border-edge/70">
            <img src={imgSrc(product.image_url)} alt={product.title} className={`h-full w-full object-cover ${sold ? "opacity-40 grayscale" : ""}`} />
            <div className="absolute left-4 top-4 bg-[#0B1526]/85 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-mutedink backdrop-blur-sm">
              [{product.code}]
            </div>
            {sold && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="rotate-[-8deg] border-2 border-signal bg-[#0B1526]/85 px-5 py-2 font-mono text-base font-bold uppercase tracking-widest text-signal">
                  {t.product.sold}
                </span>
              </div>
            )}
            {reserved && (
              <div className="absolute bottom-4 left-4 border border-reservedamber bg-[#0B1526]/85 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-reservedamber backdrop-blur-sm" data-testid="modal-reserved-badge">
                {t.product.reserved}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col p-6 md:p-8">
          <span className={`w-fit px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${conditionStyle[product.condition] || conditionStyle.vinted}`} data-testid="modal-condition-badge">
            {t.conditions[product.condition] || product.condition}
          </span>
          <div className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-volt">{product.brand}</div>
          <h3 className="mt-2 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl" data-testid="modal-product-title">{product.title}</h3>
          <p className="mt-4 text-sm leading-relaxed text-mutedink">{description}</p>
          <div className="mt-6 grid grid-cols-3 divide-x divide-edge border-y border-edge">
            <div className="py-3 pr-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-mutedink">{t.product.size}</div>
              <div className="mt-1 font-grotesk font-semibold text-ink" data-testid="modal-size">{product.size}</div>
            </div>
            <div className="px-3 py-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-mutedink">{t.product.condition}</div>
              <div className="mt-1 font-grotesk font-semibold text-ink">{t.conditions[product.condition]}</div>
            </div>
            <div className="py-3 pl-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-mutedink">{t.product.price}</div>
              <div className="mt-1 font-mono text-lg font-bold text-volt" data-testid="modal-price">€{product.price}</div>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-mutedink" data-testid="modal-one-of-one">
            <span className="h-1.5 w-1.5 bg-volt" /> {t.product.oneOfOne}
          </div>
          <div className="mt-auto flex flex-col gap-3 pt-6">
            {reserved ? (
              <div className="border border-reservedamber/50 bg-reservedamber/10 px-5 py-4 text-sm leading-relaxed text-reservedamber" data-testid="modal-reserved-note">
                {t.product.reservedNote}
              </div>
            ) : (
              <>
                <button
                  data-testid="modal-buy-vinted-button"
                  onClick={buyViaVinted}
                  disabled={sold}
                  className="flex items-center justify-center gap-2 bg-volt px-6 py-4 font-grotesk text-sm font-bold uppercase tracking-widest text-obsidian transition-colors duration-300 hover:bg-volt-hover disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <ShoppingBag size={16} />
                  {t.product.order}
                  <ExternalLink size={13} />
                </button>
                <p className="text-center text-xs leading-relaxed text-mutedink">{t.product.vintedNote}</p>
                <p className="flex items-center justify-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-mutedink" data-testid="modal-shipping-note">
                  <Truck size={12} /> {t.product.shipping}
                </p>
              </>
            )}
            <div className="flex items-center justify-center gap-5">
              <button data-testid="modal-ask-instagram-button" onClick={askInstagram} className="flex items-center gap-1.5 font-grotesk text-xs uppercase tracking-widest text-mutedink transition-colors hover:text-volt">
                <Instagram size={13} /> {t.product.askInstagram}
              </button>
              <button data-testid="modal-email-inquiry-button" onClick={emailInquiry} className="flex items-center gap-1.5 font-grotesk text-xs uppercase tracking-widest text-mutedink transition-colors hover:text-volt">
                <Mail size={13} /> {t.product.emailCta}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
